import { useState, useEffect, useRef } from 'react'
import { X, Upload, Trash2, ImageIcon, Loader } from 'lucide-react'
import { uploadProductImage, deleteProductImage, getProductImages } from '../../lib/imageService'
import { useToast } from '../../context/ToastContext'

export default function ProductImageManager({ businessKey, item, onClose }) {
  const { addToast } = useToast()
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  const [deleting, setDeleting] = useState(null)
  const fileRef = useRef(null)

  useEffect(() => {
    loadImages()
  }, [businessKey, item.id])

  async function loadImages() {
    setLoading(true)
    try {
      const imgs = await getProductImages(businessKey, item.id)
      setImages(imgs)
    } catch (err) {
      addToast('Failed to load images', 'error')
    }
    setLoading(false)
  }

  async function handleFiles(files) {
    const valid = Array.from(files).filter(f => f.type.startsWith('image/'))
    if (!valid.length) return

    for (const file of valid) {
      if (images.length >= 5) {
        addToast('Maximum 5 images per product', 'error')
        break
      }
      setUploading(true)
      setProgress(0)
      try {
        const entry = await uploadProductImage(businessKey, item.id, file, setProgress)
        setImages(prev => [...prev, entry])
        addToast('Image uploaded', 'success')
      } catch (err) {
        addToast(err.message || 'Upload failed', 'error')
      }
      setUploading(false)
    }
  }

  async function handleDelete(imageUrl) {
    setDeleting(imageUrl)
    try {
      await deleteProductImage(businessKey, item.id, imageUrl)
      setImages(prev => prev.filter(img => img.url !== imageUrl))
      addToast('Image removed', 'success')
    } catch {
      addToast('Failed to delete image', 'error')
    }
    setDeleting(null)
  }

  function onDrop(e) {
    e.preventDefault()
    setDragOver(false)
    handleFiles(e.dataTransfer.files)
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">Product Images</h3>
            <p className="text-sm text-gray-400">{item.name} — R{item.price}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        {/* Drop zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${dragOver ? 'border-brand-500 bg-brand-500/10' : 'border-gray-700 hover:border-gray-500'}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={e => handleFiles(e.target.files)}
          />
          {uploading ? (
            <div className="space-y-2">
              <Loader size={24} className="text-brand-400 animate-spin mx-auto" />
              <div className="w-full bg-gray-800 rounded-full h-2">
                <div className="bg-brand-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-gray-400">Uploading... {progress}%</p>
            </div>
          ) : (
            <>
              <Upload size={24} className="text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Drop images here or click to browse</p>
              <p className="text-xs text-gray-600 mt-1">{images.length}/5 images — JPEG, PNG, WebP</p>
            </>
          )}
        </div>

        {/* Image grid */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader size={20} className="text-gray-500 animate-spin" />
          </div>
        ) : images.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-600">
            <ImageIcon size={32} className="mb-2" />
            <p className="text-sm">No images yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {images.map((img, i) => (
              <div key={img.url} className="relative group aspect-square bg-gray-800 rounded overflow-hidden">
                <img src={img.url} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => {
                    if (deleting === img.url) return
                    if (confirm('Delete this image?')) handleDelete(img.url)
                  }}
                  disabled={deleting === img.url}
                  className="absolute top-1 right-1 w-7 h-7 bg-red-600 hover:bg-red-500 text-white rounded-full flex items-center justify-center transition-opacity"
                >
                  {deleting === img.url ? <Loader size={12} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
                <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">
                  {i + 1}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
